import { useState, useEffect, useRef, useMemo } from "react";
import { sqlRead, sqlEscape } from "../utils/sqlClient";
import { divisionLabel } from "../config/csiDivisions";

// Shared CostCodes search-and-pick combobox + "add a new one" flyout —
// extracted from src/pages/QuoteLogSetup.jsx so any admin page that anchors
// its own catalog to a real CostCodes row (Quote Log Setup's Categories,
// the Condition Library's condition types, …) can reuse the exact same
// picker instead of re-implementing it. CostCodes is the single source of
// truth for CSI structure across the app — see CLAUDE.md.

const ctl = { padding: "6px 8px", fontSize: 12.5, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", width: "100%", boxSizing: "border-box" };

// A row's CSI code + description — shared between Search and Browse mode's list items.
function CodeRow({ c, onPick }) {
  return (
    <div
      onMouseDown={onPick}
      style={{ padding: "6px 10px", fontSize: 12, cursor: "pointer" }}
      onMouseEnter={e => e.currentTarget.style.background = "var(--surface2)"}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >
      <span style={{ fontFamily: "monospace", fontWeight: 600, marginRight: 8 }}>{c.CSICode}</span>
      <span style={{ color: "var(--text2)" }}>{c.Description}</span>
    </div>
  );
}

export function CostCodePicker({ value, costCodes, onSelect }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  // Search (type-to-filter) is the default and handles a known code fastest;
  // Browse groups the full catalog by CSI division — same idea as OST's own
  // Condition Properties dialog, which picks a type by expanding its
  // division tree rather than typing (see the "how do I do a screen
  // recording" session, 2026-08-26).
  const [mode, setMode]   = useState("search");
  const [openDivisions, setOpenDivisions] = useState(new Set());
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current  = costCodes.find(c => c.CostCodeID === value);
  const q        = query.trim().toLowerCase();
  const filtered = (q ? costCodes.filter(c => `${c.CSICode} ${c.Description}`.toLowerCase().includes(q)) : costCodes).slice(0, 30);

  // sp_GetCostCodes already orders by CSICodeNumeric, so codes land in each
  // division bucket pre-sorted — no re-sort needed, just group.
  const byDivision = useMemo(() => {
    const map = new Map();
    for (const c of costCodes) {
      const key = c.Division ?? null;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    return [...map.entries()].sort((a, b) => (a[0] ?? 999) - (b[0] ?? 999));
  }, [costCodes]);

  function toggleDivision(key) {
    setOpenDivisions(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  function pick(c) {
    onSelect(c);
    setOpen(false);
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        value={open && mode === "search" ? query : (current ? `${current.CSICode} — ${current.Description}` : "")}
        readOnly={mode === "browse"}
        onFocus={() => { setOpen(true); if (mode === "search") setQuery(""); }}
        onChange={e => setQuery(e.target.value)}
        placeholder="Search CSI code or description…"
        style={{ ...ctl, fontSize: 12 }}
      />
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 2px)", left: 0, minWidth: 320, zIndex: 50,
          background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 6,
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)", maxHeight: 280, overflowY: "auto",
        }}>
          <div style={{ display: "flex", gap: 4, padding: 6, borderBottom: "1px solid var(--cs-border)", position: "sticky", top: 0, background: "var(--surface)" }}>
            {["search", "browse"].map(m => (
              <button
                key={m}
                onMouseDown={e => e.preventDefault()}
                onClick={() => setMode(m)}
                style={{
                  flex: 1, padding: "4px 8px", fontSize: 11, fontWeight: 700, textTransform: "capitalize",
                  border: "1px solid var(--cs-border)", borderRadius: 5, cursor: "pointer",
                  background: mode === m ? "var(--cs-blue)" : "var(--surface)",
                  color: mode === m ? "#fff" : "var(--text2)",
                }}
              >{m}</button>
            ))}
          </div>

          {mode === "search" && (
            <>
              {filtered.length === 0 && <div style={{ padding: "8px 10px", fontSize: 12, color: "var(--text3)" }}>No matches</div>}
              {filtered.map(c => <CodeRow key={c.CostCodeID} c={c} onPick={() => pick(c)} />)}
            </>
          )}

          {mode === "browse" && byDivision.map(([division, codes]) => {
            const divOpen = openDivisions.has(division);
            return (
              <div key={division ?? "none"}>
                <div
                  onMouseDown={e => { e.preventDefault(); toggleDivision(division); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", cursor: "pointer",
                    fontSize: 11.5, fontWeight: 700, color: "var(--text2)", background: "var(--surface2)",
                    borderTop: "1px solid var(--cs-border)",
                  }}
                >
                  <span style={{ display: "inline-block", transform: divOpen ? "rotate(90deg)" : "none", transition: "transform 0.12s", fontSize: 9, color: "var(--text3)" }}>▸</span>
                  {divisionLabel(division)}
                  <span style={{ marginLeft: "auto", fontWeight: 400, color: "var(--text3)" }}>{codes.length}</span>
                </div>
                {divOpen && codes.map(c => <CodeRow key={c.CostCodeID} c={c} onPick={() => pick(c)} />)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const flyoutLabel = { fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 3 };
const flyoutInput = { height: 30, width: "100%", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 9px", fontSize: 12.5, color: "var(--text)", background: "var(--surface)", outline: "none", boxSizing: "border-box" };

// Adds a brand-new row to CostCodes itself (the shared reference table also
// read by Bid Packages) — for a real MasterFormat code not cataloged yet, or
// a company-specific extension under one (e.g. "13 21 13.01"). There's no
// other write path to this table, so this is the only way a code that
// doesn't show up in CostCodePicker's search gets added.
export function AddCostCodeFlyout({ onClose, onSaved }) {
  const [form, setForm] = useState({
    csi_code: "", description: "", division: "", csi_level: "Level 4",
    tier: "Core", estimating_trade: "", bid_package: "", default_unit: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function onCsiCodeChange(v) {
    set("csi_code", v);
    const div = parseInt(v.trim().split(/\s+/)[0], 10);
    if (!isNaN(div)) set("division", String(div));
  }

  async function handleSave() {
    if (!form.csi_code.trim() || !form.description.trim()) {
      setError("CSI Code and Description are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        csi_code:         form.csi_code.trim(),
        description:      form.description.trim(),
        division:         form.division ? +form.division : null,
        csi_level:        form.csi_level || null,
        tier:             form.tier || null,
        estimating_trade: form.estimating_trade.trim() || null,
        bid_package:      form.bid_package.trim() || null,
        default_unit:     form.default_unit.trim() || null,
      };
      const result = await sqlRead(`EXEC sp_SaveCostCode @payload='${sqlEscape(JSON.stringify(payload))}'`);
      if (!result?.[0]?.Success) {
        setError(result?.[0]?.ErrorMessage || "Save failed");
        return;
      }
      onSaved({
        CostCodeID: result[0].CostCodeID,
        CSICode: payload.csi_code,
        CSICodeNumeric: payload.csi_code.replace(/[^0-9]/g, ""),
        Division: payload.division,
        Description: payload.description,
        CSILevel: payload.csi_level,
        Tier: payload.tier,
        EstimatingTrade: payload.estimating_trade,
        BidPackage: payload.bid_package,
        DefaultUnit: payload.default_unit,
        IsActive: true,
      });
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, width: 380, height: "100vh",
      background: "var(--surface)", borderLeft: "1px solid var(--cs-border)",
      display: "flex", flexDirection: "column",
      boxShadow: "-4px 0 24px rgba(0,0,0,0.07)", zIndex: 400,
    }}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--cs-border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>Add Cost Code</div>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "var(--text3)", padding: "2px 6px" }}>✕</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
        <p style={{ fontSize: 12, color: "var(--text3)", margin: 0 }}>
          Adds a new row to CostCodes — the shared CSI reference table also used by Bid Packages. Use this for a
          real MasterFormat code that isn't cataloged yet, or a company-specific extension under one (e.g.
          "13 21 13.01").
        </p>
        <div>
          <label style={flyoutLabel}>CSI Code *</label>
          <input style={{ ...flyoutInput, fontFamily: "monospace" }} value={form.csi_code} placeholder="e.g. 43 11 43"
            onChange={e => onCsiCodeChange(e.target.value)} />
        </div>
        <div>
          <label style={flyoutLabel}>Description *</label>
          <input style={flyoutInput} value={form.description} placeholder="e.g. Gas-handling Vacuum Pumps"
            onChange={e => set("description", e.target.value)} />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={flyoutLabel}>Division</label>
            <input style={flyoutInput} type="number" value={form.division} onChange={e => set("division", e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={flyoutLabel}>CSI Level</label>
            <select style={flyoutInput} value={form.csi_level} onChange={e => set("csi_level", e.target.value)}>
              {["Division", "Level 2", "Level 3", "Level 4", "Level 5"].map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label style={flyoutLabel}>Tier</label>
          <select style={flyoutInput} value={form.tier} onChange={e => set("tier", e.target.value)}>
            {["Core", "Secondary", "Reference"].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label style={flyoutLabel}>Estimating Trade</label>
          <input style={flyoutInput} value={form.estimating_trade} placeholder="e.g. Process Gas & Liquid Equipment"
            onChange={e => set("estimating_trade", e.target.value)} />
        </div>
        <div>
          <label style={flyoutLabel}>Bid Package</label>
          <input style={flyoutInput} value={form.bid_package} placeholder="e.g. Process Gas / Liquid Handling"
            onChange={e => set("bid_package", e.target.value)} />
        </div>
        <div>
          <label style={flyoutLabel}>Default Unit (optional)</label>
          <input style={flyoutInput} value={form.default_unit} onChange={e => set("default_unit", e.target.value)} />
        </div>
      </div>

      <div style={{ padding: "12px 18px", borderTop: "1px solid var(--cs-border)", display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
        {error && (
          <div style={{ fontSize: 12, color: "var(--red, #dc2626)", padding: "6px 10px", background: "var(--tint-red)", borderRadius: "var(--radius)" }}>
            {error}
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={{
            flex: 1, height: 34, fontSize: 13, fontWeight: 500,
            border: "1px solid var(--cs-border)", borderRadius: "var(--radius)",
            background: "none", color: "var(--text2)", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            flex: 2, height: 34, fontSize: 13, fontWeight: 600,
            border: "none", borderRadius: "var(--radius)",
            background: saving ? "var(--cs-border)" : "var(--cs-blue)",
            color: "#fff", cursor: saving ? "default" : "pointer",
          }}>{saving ? "Saving…" : "Add Cost Code"}</button>
        </div>
      </div>
    </div>
  );
}
